import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { jmd } from "@/lib/format";
import { Crown, Check, Truck, Zap, Star, Hotel, Headset, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Truck, t: "Free Standard Delivery", s: "On every order, worldwide — no minimum spend." },
  { icon: Zap, t: "Early Access to Deals", s: "Shop flash deals and drops before everyone else." },
  { icon: Star, t: "Bonus SHOP Points", s: "Instant 1,000-point welcome bonus + boosted earning." },
  { icon: Hotel, t: "Hotel & Stay Discounts", s: "Member rates on select Jamaican hotels and villas." },
  { icon: Headset, t: "Priority Support", s: "Skip the queue with priority customer care." },
  { icon: Crown, t: "Exclusive Jamaica Made", s: "First dibs on limited local artisan products." },
];

export default function ShopPlus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) api.get("/membership/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user]);

  const subscribe = async () => {
    if (!user) { navigate("/login"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/membership/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); setLoading(false); }
  };

  const price = status?.price_jmd || 1500;
  const active = status?.active;

  return (
    <div>
      <section className="relative overflow-hidden sn-noise" style={{ background: "linear-gradient(120deg,#0A0A0C,#8E0E13)" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-white relative text-center">
          <span className="inline-flex items-center gap-2 sn-glass sn-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"><Crown size={14} className="text-gold" /> SHOP+ Membership</span>
          <h1 className="font-clash font-bold text-4xl sm:text-6xl mt-5">Shop more. Save more.<br />Get more.</h1>
          <p className="text-white/70 mt-4 max-w-xl mx-auto text-lg">Unlock free delivery, early deals, bonus points and member-only perks across SHOP NOW Jamaica.</p>
          {active ? (
            <div className="mt-8 inline-flex items-center gap-2 bg-gold text-ink font-bold rounded-full px-6 py-3" data-testid="shop-plus-active-badge">
              <Crown size={18} /> You're a SHOP+ member{status?.expiry ? ` · renews ${new Date(status.expiry).toLocaleDateString()}` : ""}
            </div>
          ) : (
            <button onClick={subscribe} disabled={loading} data-testid="shop-plus-subscribe-btn"
              className="mt-8 inline-flex items-center gap-2 bg-emerald text-white font-bold rounded-full px-8 py-4 text-lg hover:bg-white hover:text-ink transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Crown size={20} />} Join for {jmd(price)}/month
            </button>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-3xl font-bold text-center mb-10">Everything included in SHOP+</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b) => (
            <div key={b.t} className="bg-card rounded-2xl border border-border p-6 sn-card-hover">
              <span className="h-12 w-12 rounded-xl bg-emerald/10 text-emerald grid place-items-center mb-4"><b.icon size={22} /></span>
              <h3 className="font-display font-bold flex items-center gap-2">{b.t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{b.s}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 max-w-2xl mx-auto bg-card rounded-3xl border border-border p-8 text-center">
          <Crown size={40} className="mx-auto text-gold mb-3" />
          <div className="font-display font-bold text-2xl">{jmd(price)} <span className="text-base font-normal text-muted-foreground">/ month</span></div>
          <ul className="text-sm text-slate-700 mt-4 space-y-2 text-left max-w-sm mx-auto">
            {["Free standard delivery on all orders", "1,000 welcome SHOP Points", "Early access to flash deals", "Member hotel rates & priority support"].map((x) => (
              <li key={x} className="flex items-center gap-2"><Check size={16} className="text-emerald" /> {x}</li>
            ))}
          </ul>
          {!active && (
            <button onClick={subscribe} disabled={loading} className="mt-6 w-full bg-ink text-white rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : null} Become a SHOP+ Member
            </button>
          )}
          <p className="text-xs text-muted-foreground mt-3">Cancel anytime · Billed monthly via secure Stripe checkout</p>
        </div>
      </div>
    </div>
  );
}
